import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Bot,
  User,
  Trash2,
  Sparkles,
  ShieldAlert,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AI_CONSENT_KEY = 'ai_chatbot_consent_v1';

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [hasConsent, setHasConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load suggestions on mount
  useEffect(() => {
    fetchSuggestions();
  }, []);

  // Check for stored AI consent on mount
  useEffect(() => {
    const stored = localStorage.getItem(AI_CONSENT_KEY);
    if (stored === 'granted') {
      setHasConsent(true);
    }
  }, []);

  const handleOpenChatbot = () => {
    // Leggi sempre da localStorage per gestire il caso in cui il consenso sia stato appena dato/revocato
    const stored = localStorage.getItem(AI_CONSENT_KEY);
    if (stored !== 'granted') {
      setShowConsentModal(true);
    } else {
      setHasConsent(true);
      setIsOpen(true);
    }
  };

  const grantConsent = () => {
    localStorage.setItem(AI_CONSENT_KEY, 'granted');
    setHasConsent(true);
    setShowConsentModal(false);
    setIsOpen(true);
  };

  const revokeConsent = () => {
    localStorage.removeItem(AI_CONSENT_KEY);
    setHasConsent(false);
    setShowConsentModal(false);
  };

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/suggestions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        const botMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const botMessage = {
          role: 'assistant',
          content: 'Mi dispiace, si è verificato un errore. Riprova più tardi.',
          timestamp: new Date().toISOString(),
          isError: true
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const botMessage = {
        role: 'assistant',
        content: 'Errore di connessione. Verifica la tua connessione internet.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, botMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/chatbot/clear-session?session_id=${sessionId}`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (error) {
        console.error('Error clearing session:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
          return `<li key=${i}>${line.substring(2)}</li>`;
        }
        // Numbered lists
        if (/^\d+\.\s/.test(line.trim())) {
          return `<li key=${i}>${line.replace(/^\d+\.\s/, '')}</li>`;
        }
        return line ? `<p key=${i}>${line}</p>` : '<br/>';
      })
      .join('');
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={handleOpenChatbot}
          className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-br from-[#0055BB] to-[#003d88] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
          data-testid="chatbot-toggle"
          aria-label="Apri assistente virtuale AI"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 text-[10px] font-bold bg-white text-[#0055BB] rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center border border-[#0055BB]">
            AI
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-slate-200 overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0055BB] to-[#003d88] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center relative">
                <Bot className="w-6 h-6" />
                <span className="absolute -bottom-1 -right-1 text-[9px] font-bold bg-white text-[#0055BB] rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center border border-[#0055BB]">
                  AI
                </span>
              </div>
              <div>
                <h3 className="font-semibold" data-testid="chatbot-title">Assistente virtuale AI</h3>
                <p className="text-xs opacity-90">Chatbot basato su intelligenza artificiale</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Nuova conversazione"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.length === 0 && (
              <>
                {/* AI Transparency Notice (Art. 50 AI Act) */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-2" data-testid="ai-transparency-notice">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                    <div className="space-y-1">
                      <p className="font-semibold">Trasparenza AI (Art. 50 Reg. UE 2024/1689):</p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        <li>Sto usando <strong>OpenAI GPT-4o-mini</strong> per generare le risposte</li>
                        <li>Le risposte possono contenere errori: verifica sempre le informazioni critiche</li>
                        <li>Non fornisco consulenza legale/fiscale personalizzata</li>
                        <li>Non inserire dati personali di terzi o dati sensibili</li>
                      </ul>
                      <p className="pt-1">
                        <a href="/uso-intelligenza-artificiale" target="_blank" rel="noreferrer" className="underline font-medium">Maggiori informazioni</a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center py-2">
                  <div className="w-16 h-16 bg-[#0055BB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-[#0055BB]" />
                  </div>
                  <h4 className="font-medium text-slate-800 mb-2">Ciao! Come posso aiutarti?</h4>
                  <p className="text-sm text-slate-500 mb-4">Chiedimi qualsiasi cosa su Assietica Gest</p>
                  
                  {/* Quick Suggestions */}
                  <div className="space-y-2">
                    {suggestions.slice(0, 4).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => sendMessage(suggestion)}
                        className="w-full text-left text-sm px-3 py-2 bg-white rounded-lg border border-slate-200 hover:border-[#0055BB] hover:bg-blue-50 transition-colors text-slate-700"
                      >
                        {suggestion}
                      </button>
                  ))}
                </div>
              </div>
              </>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-[#0055BB] text-white' 
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {message.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className={`px-4 py-2 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-[#0055BB] text-white rounded-tr-sm'
                      : message.isError 
                        ? 'bg-red-100 text-red-800 rounded-tl-sm'
                        : 'bg-white text-slate-800 rounded-tl-sm shadow-sm border border-slate-100'
                  }`}>
                    {message.role === 'assistant' ? (
                      <div 
                        className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-li:my-0.5"
                        dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                      />
                    ) : (
                      <p className="text-sm">{message.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-slate-600" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm border border-slate-100">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-200">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-2 border border-slate-200 rounded-full focus:outline-none focus:border-[#0055BB] focus:ring-2 focus:ring-[#0055BB]/20 text-sm"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                className="w-10 h-10 rounded-full bg-[#0055BB] hover:bg-[#003d88] p-0"
                data-testid="chatbot-send"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center mt-2" data-testid="chatbot-footer-disclaimer">
              Contenuti generati automaticamente da AI (OpenAI GPT-4o-mini) — Verifica sempre le informazioni critiche
            </p>
          </div>
        </div>
      )}

      {/* AI Consent Modal (mostrato al primo utilizzo) */}
      {showConsentModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          data-testid="ai-consent-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0055BB] to-[#003d88] text-white p-5 flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center relative shrink-0">
                <Bot className="w-6 h-6" />
                <span className="absolute -bottom-1 -right-1 text-[10px] font-bold bg-white text-[#0055BB] rounded-full h-4 min-w-[18px] px-1 flex items-center justify-center">
                  AI
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Prima di iniziare</h3>
                <p className="text-xs opacity-90">Informativa sull&apos;uso dell&apos;Intelligenza Artificiale</p>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 text-sm text-slate-700">
              <p>
                Il chatbot di assistenza di AssieticaGest è un&apos;<strong>Intelligenza Artificiale</strong>
                {' '}(sistema conforme al Regolamento UE 2024/1689 — AI Act).
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <div className="space-y-1.5 text-xs">
                    <p className="font-semibold text-amber-900">Cosa devi sapere:</p>
                    <ul className="list-disc pl-4 space-y-1 text-amber-900">
                      <li>I tuoi messaggi vengono elaborati dal modello <strong>OpenAI GPT-4o-mini</strong> (server negli USA)</li>
                      <li>Il trasferimento dati avviene tramite Clausole Contrattuali Standard UE (SCC)</li>
                      <li>Le risposte possono contenere <strong>errori o inesattezze</strong></li>
                      <li>Il chatbot <strong>non fornisce</strong> consulenza legale, fiscale o normativa personalizzata</li>
                      <li>Non condividere dati personali di terzi (soci, volontari) o dati sensibili</li>
                      <li>Non salviamo le tue conversazioni oltre la sessione</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Per approfondimenti consulta la nostra {' '}
                <a href="/uso-intelligenza-artificiale" target="_blank" rel="noreferrer" className="text-[#0055BB] underline font-medium">
                  pagina dedicata sull&apos;uso dell&apos;AI
                </a>
                {' '}e la{' '}
                <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#0055BB] underline font-medium">
                  Privacy Policy
                </a>.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={revokeConsent}
                className="flex-1"
                data-testid="ai-consent-decline"
              >
                Non ora
              </Button>
              <Button
                onClick={grantConsent}
                className="flex-1 bg-[#0055BB] hover:bg-[#003d88]"
                data-testid="ai-consent-accept"
              >
                Ho letto e accetto
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
